import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Breadcrumb, BackdropCircular } from 'components';
import {
  Card,
  Button,
  Tab,
  Tabs,
  Badge
} from '@material-ui/core';
import { Formik } from 'formik';
import {
  getBeforeAfterGalleryConfigsApi,
  saveBeforeAfterGalleryConfigsApi,
  getAssetManagerCategoryTagsApi,
  getAssetManagerImagesApi
} from 'api';
import GalleryGeneral from './GalleryGeneral';
import GalleryDisplay from './GalleryDisplay';
import GallerySurgeons from './GallerySurgeons';
import GalleryTabSections from './GalleryTabSections';
import useGalleryAssetTags from './useGalleryAssetTags';
import { validationSchema } from './validation';
import {
  GALLERY_TAB_CODES,
  GALLERY_TAB_DEFAULT_LABELS,
  GALLERY_TAB_DEFAULT_ENABLED,
  SLIDER_ASSET_TAG_CODE,
  TAB_ASSET_TAG_CODE,
  GENERAL_CONFIG_PATHS,
  DISPLAY_CONFIG_PATHS,
  SURGEONS_CONFIG_PATH,
  getTabEnabledConfigPath,
  getTabLabelConfigPath,
  getTabImageConfigPath,
  getTabSectionsConfigPath,
  configValue,
  configFlag,
  parseJsonConfig,
  normalizeSurgeon,
  normalizeSection,
  serializeSurgeons,
  serializeSections
} from './constants';


const staticTabs = [
  'General',
  'Display',
  'Surgeons'
];

const BeforeAfterGalleryBlock = () => {
  const dispatch = useDispatch();
  const [tabIndex, setTabIndex] = useState(0);
  const [configsLoaded, setConfigsLoaded] = useState(false);

  const {
    beforeAfterGalleryConfigs,
    loadingBeforeAfterGalleryConfigs,
    savingBeforeAfterGalleryConfigs
  } = useSelector((state: IReduxState) => state.store_configs);

  const {
    assetCategoryTags,
    loadingAssetCategoryTags
  } = useSelector((state: IReduxState) => state.asset_manager);

  const { ownImagesTagIds } = useGalleryAssetTags();
  const { ownImagesTagIds: sliderOwnImagesTagIds } = useGalleryAssetTags(SLIDER_ASSET_TAG_CODE);
  const { ownImagesTagIds: tabOwnImagesTagIds } = useGalleryAssetTags(TAB_ASSET_TAG_CODE);

  const loading = loadingBeforeAfterGalleryConfigs
    || savingBeforeAfterGalleryConfigs
    || loadingAssetCategoryTags;

  useEffect(() => {
    (async () => {
      await getBeforeAfterGalleryConfigsApi({dispatch});
      setConfigsLoaded(true);
    })();

    if (!assetCategoryTags.length) {
      getAssetManagerCategoryTagsApi({dispatch, categoryName: 'Storebuild-automaton'});
    }
  }, []);

  useEffect(() => {
    if (ownImagesTagIds.length) {
      getAssetManagerImagesApi({
        dispatch,
        limit: 20,
        page: 1,
        tagIds: ownImagesTagIds,
        isPrivate: true
      });
    }
  }, [ownImagesTagIds]);

  useEffect(() => {
    if (sliderOwnImagesTagIds.length) {
      getAssetManagerImagesApi({
        dispatch,
        limit: 20,
        page: 1,
        tagIds: sliderOwnImagesTagIds,
        isPrivate: true
      });
    }
  }, [sliderOwnImagesTagIds]);

  useEffect(() => {
    if (tabOwnImagesTagIds.length) {
      getAssetManagerImagesApi({
        dispatch,
        limit: 20,
        page: 1,
        tagIds: tabOwnImagesTagIds,
        isPrivate: true
      });
    }
  }, [tabOwnImagesTagIds]);

  const buildInitialValues = () => ({
    general: {
      enabled: configFlag(beforeAfterGalleryConfigs, GENERAL_CONFIG_PATHS.enabled, 0),
      url_key: configValue(beforeAfterGalleryConfigs, GENERAL_CONFIG_PATHS.url_key, 'before-after'),
      page_title: configValue(beforeAfterGalleryConfigs, GENERAL_CONFIG_PATHS.page_title, 'Before and After Photos'),
      description: configValue(beforeAfterGalleryConfigs, GENERAL_CONFIG_PATHS.description),
      meta_title: configValue(beforeAfterGalleryConfigs, GENERAL_CONFIG_PATHS.meta_title),
      meta_description: configValue(beforeAfterGalleryConfigs, GENERAL_CONFIG_PATHS.meta_description),
      meta_keywords: configValue(beforeAfterGalleryConfigs, GENERAL_CONFIG_PATHS.meta_keywords)
    },
    display: {
      show_surgeon_filter: configFlag(beforeAfterGalleryConfigs, DISPLAY_CONFIG_PATHS.show_surgeon_filter, 1),
      surgeon_filter_label: configValue(beforeAfterGalleryConfigs, DISPLAY_CONFIG_PATHS.surgeon_filter_label, 'Filter by Surgeon'),
      surgeon_filter_all_label: configValue(beforeAfterGalleryConfigs, DISPLAY_CONFIG_PATHS.surgeon_filter_all_label, 'All Surgeons'),
      show_tag_filter: configFlag(beforeAfterGalleryConfigs, DISPLAY_CONFIG_PATHS.show_tag_filter, 0),
      tag_filter_label: configValue(beforeAfterGalleryConfigs, DISPLAY_CONFIG_PATHS.tag_filter_label, 'Filter by Tag'),
      tag_filter_all_label: configValue(beforeAfterGalleryConfigs, DISPLAY_CONFIG_PATHS.tag_filter_all_label, 'All Tags'),
      show_attributes: configFlag(beforeAfterGalleryConfigs, DISPLAY_CONFIG_PATHS.show_attributes, 1),
      show_item_titles: configFlag(beforeAfterGalleryConfigs, DISPLAY_CONFIG_PATHS.show_item_titles, 0),
      before_label: configValue(beforeAfterGalleryConfigs, DISPLAY_CONFIG_PATHS.before_label, 'Before'),
      after_label: configValue(beforeAfterGalleryConfigs, DISPLAY_CONFIG_PATHS.after_label, 'After')
    },
    surgeons: parseJsonConfig(beforeAfterGalleryConfigs[SURGEONS_CONFIG_PATH]).map(normalizeSurgeon),
    tabs: GALLERY_TAB_CODES.reduce((tabs, tabCode) => ({
      ...tabs,
      [tabCode]: {
        enabled: configFlag(beforeAfterGalleryConfigs, getTabEnabledConfigPath(tabCode), GALLERY_TAB_DEFAULT_ENABLED[tabCode]),
        label: configValue(beforeAfterGalleryConfigs, getTabLabelConfigPath(tabCode), GALLERY_TAB_DEFAULT_LABELS[tabCode]),
        image: configValue(beforeAfterGalleryConfigs, getTabImageConfigPath(tabCode)),
        sections: parseJsonConfig(beforeAfterGalleryConfigs[getTabSectionsConfigPath(tabCode)]).map(normalizeSection)
      }
    }), {})
  });

  const handleSave = (values) => {
    const configs = [
      ...Object.entries(GENERAL_CONFIG_PATHS).map(([key, path]) => ({path, value: values.general[key]})),
      ...Object.entries(DISPLAY_CONFIG_PATHS).map(([key, path]) => ({path, value: values.display[key]})),
      {path: SURGEONS_CONFIG_PATH, value: serializeSurgeons(values.surgeons)},
      ...GALLERY_TAB_CODES.reduce((tabConfigs, tabCode) => [
        ...tabConfigs,
        {path: getTabEnabledConfigPath(tabCode), value: values.tabs[tabCode].enabled},
        {path: getTabLabelConfigPath(tabCode), value: values.tabs[tabCode].label},
        {path: getTabImageConfigPath(tabCode), value: values.tabs[tabCode].image},
        {path: getTabSectionsConfigPath(tabCode), value: serializeSections(values.tabs[tabCode].sections)}
      ], [])
    ];

    saveBeforeAfterGalleryConfigsApi({dispatch, configs});
  };

  const handleTabChange = (e, value) => {
    setTabIndex(value);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setTabLabel = (tabName, errors: any) => {
    if (tabName === 'Surgeons' && errors?.surgeons) {
      return (
        <Badge badgeContent={'!'} color="error">
          {tabName}
        </Badge>
      );
    }

    return tabName;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setGalleryTabLabel = (tabCode, values, errors: any) => {
    const title = values.tabs[tabCode].label || GALLERY_TAB_DEFAULT_LABELS[tabCode];

    if (errors?.tabs?.[tabCode]) {
      return (
        <Badge badgeContent={'!'} color="error">
          {title}
        </Badge>
      );
    }

    return title;
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <Breadcrumb
        routeSegments={[
          { name: 'Settings', path: '/settings#go_live_requirements' },
          { name: 'UI Configurations', path: '/settings#UI_configurations' },
          { name: 'Before/After Gallery'}
        ]}
      />
      <div className="m-10">
        {configsLoaded &&
          <Formik
            onSubmit={(values) => handleSave(values)}
            initialValues={buildInitialValues()}
            validationSchema={validationSchema}
          >
            {({
              values,
              errors,
              setFieldValue,
              handleSubmit
            }) => (
              <form autoComplete="off" onSubmit={handleSubmit}>
                <Card className="p-8">
                  <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    className="mb-8"
                  >
                    {staticTabs.map((tab, idx) => (
                      <Tab
                        className="capitalize font-medium"
                        key={`tab-${idx}`}
                        value={idx}
                        label={setTabLabel(tab, errors)}
                      />
                    ))}
                    {GALLERY_TAB_CODES.map((tabCode, idx) => (
                      <Tab
                        className="capitalize font-medium"
                        key={`tab-${tabCode}`}
                        value={staticTabs.length + idx}
                        label={setGalleryTabLabel(tabCode, values, errors)}
                      />
                    ))}
                  </Tabs>
                  {tabIndex === 0 && <GalleryGeneral values={values} onChange={setFieldValue} />}
                  {tabIndex === 1 && <GalleryDisplay values={values} onChange={setFieldValue} />}
                  {tabIndex === 2 && <GallerySurgeons values={values} onChange={setFieldValue} errors={errors} />}
                  {tabIndex >= staticTabs.length &&
                    <GalleryTabSections
                      tabCode={GALLERY_TAB_CODES[tabIndex - staticTabs.length]}
                      values={values}
                      onChange={setFieldValue}
                      errors={errors}
                    />
                  }
                </Card>
                <div className="flex justify-end">
                  <Button
                    variant="contained"
                    color="primary"
                    className="my-10"
                    type="submit"
                  >
                    Save Configurations
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        }
      </div>
      <BackdropCircular loading={loading} />
    </div>
  );
};

export default BeforeAfterGalleryBlock;
